import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress,
  Avatar,
  Icon,
  Flex,
  Spacer,
  Grid,
  GridItem,
  useColorModeValue,
  Circle,
  Tooltip,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  DownloadIcon,
  EmailIcon,
  CheckCircleIcon,
  TimeIcon,
  WarningIcon,
  InfoIcon,
  CalendarIcon,
  PhoneIcon,
  EmailIcon as EmailOutlineIcon,
  StarIcon,
  LockIcon,
  ViewIcon,
  ViewOffIcon,
} from "@chakra-ui/icons";
import {
  FaUser,
  FaBuilding,
  FaMoneyBillWave,
  FaFileContract,
  FaHistory,
  FaClock,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import offerService from "../services/offerService";

const OfferDetailsModal = ({ isOpen, onClose, offer }) => {
  const [offerData, setOfferData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCompensationPrivate, setIsCompensationPrivate] = useState(false);

  useEffect(() => {
    if (isOpen && offer) {
      fetchOfferDetails();
    }
  }, [isOpen, offer]);

  const fetchOfferDetails = async () => {
    if (!offer?._id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await offerService.getOfferById(offer._id);
      setOfferData(response.data.data);
    } catch (error) {
      console.error("Error fetching offer details:", error);
      setError("Failed to fetch offer details");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: {
        color: "gray",
        icon: InfoIcon,
        label: "Draft",
        bg: "gray.50",
        color: "gray.700",
      },
      pending_approval: {
        color: "orange",
        icon: TimeIcon,
        label: "Pending Approval",
        bg: "orange.50",
        color: "orange.700",
      },
      approved: {
        color: "blue",
        icon: CheckCircleIcon,
        label: "Approved",
        bg: "blue.50",
        color: "blue.700",
      },
      sent: {
        color: "purple",
        icon: EmailIcon,
        label: "Sent",
        bg: "purple.50",
        color: "purple.700",
      },
      pending_acceptance: {
        color: "yellow",
        icon: WarningIcon,
        label: "Pending Acceptance",
        bg: "yellow.50",
        color: "yellow.700",
      },
      accepted: {
        color: "green",
        icon: CheckCircleIcon,
        label: "Accepted",
        bg: "green.50",
        color: "green.700",
      },
      rejected: {
        color: "red",
        icon: WarningIcon,
        label: "Rejected",
        bg: "red.50",
        color: "red.700",
      },
      expired: {
        color: "red",
        icon: WarningIcon,
        label: "Expired",
        bg: "red.50",
        color: "red.700",
      },
      withdrawn: {
        color: "gray",
        icon: InfoIcon,
        label: "Withdrawn",
        bg: "gray.50",
        color: "gray.700",
      },
      cancelled: {
        color: "gray",
        icon: InfoIcon,
        label: "Cancelled",
        bg: "gray.50",
        color: "gray.700",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <Badge
        bg={config.bg}
        color={config.color}
        px={3}
        py={1}
        borderRadius="full"
        fontSize="sm"
        fontWeight="medium"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Icon as={config.icon} w={3} h={3} />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount, currency = "INR") => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const maskSensitiveData = (value, isPrivate = false) => {
    if (isPrivate && typeof value === "string" && value.includes("₹")) {
      return "₹••••••";
    }
    if (isPrivate && typeof value === "number") {
      return "••••••";
    }
    return value;
  };

  const handleDownloadPDF = () => {
    if (offerData?.header?.documentUrl) {
      window.open(offerData.header.documentUrl, "_blank");
    }
  };

  const handleResendEmail = async () => {
    try {
      await offerService.resendOffer(offer._id);
      // Show success message
    } catch (error) {
      console.error("Error resending offer:", error);
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.100" />
        <ModalContent
          borderRadius="xl"
          overflow="hidden"
          boxShadow="xl"
          border="1px"
          borderColor="blue.100"
        >
          <ModalBody>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              h="300px"
              gap={4}
            >
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text color="gray.600" fontSize="md" fontWeight="medium">
                Loading offer details...
              </Text>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.100" />
        <ModalContent
          borderRadius="xl"
          overflow="hidden"
          boxShadow="xl"
          border="1px"
          borderColor="red.100"
        >
          <ModalBody>
            <Alert status="error" borderRadius="xl" variant="subtle">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Error Loading Offer Details</Text>
                <Text>{error}</Text>
              </Box>
            </Alert>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (!offerData) return null;

  const { header, details, clauses, acceptance, recentLogs } = offerData;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent
        borderRadius="lg"
        overflow="hidden"
        boxShadow="lg"
        maxH="85vh"
        bg={useColorModeValue("white", "gray.800")}
      >
        <ModalHeader
          bg={useColorModeValue(
            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "gray.700"
          )}
          color="white"
          borderBottom="1px"
          borderColor={useColorModeValue("gray.200", "gray.600")}
          py={5}
        >
          <Flex align="center" justify="space-between" w="full">
            <Box>
              <Text fontSize="xl" fontWeight="bold" color="white">
                {header.offerId}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.800" opacity={0.9}>
                {header.candidateId?.name || "NA"} •{" "}
                {header.requisitionId?.jobTitle || "NA"}
              </Text>
            </Box>
            <HStack spacing={3}>
              {getStatusBadge(header.status)}
              <HStack spacing={2}>
                {header.documentUrl && (
                  <Tooltip label="Download Offer PDF">
                    <Button
                      leftIcon={<DownloadIcon />}
                      size="sm"
                      colorScheme="blue"
                      variant="solid"
                      onClick={handleDownloadPDF}
                      borderRadius="md"
                    >
                      Download
                    </Button>
                  </Tooltip>
                )}
                {(header.status === "sent" ||
                  header.status === "pending_acceptance") && (
                  <Tooltip label="Resend Offer Email">
                    <Button
                      leftIcon={<EmailIcon />}
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={handleResendEmail}
                      borderRadius="md"
                    >
                      Resend
                    </Button>
                  </Tooltip>
                )}
              </HStack>
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0} maxH="calc(85vh - 100px)" overflowY="auto">
          <Tabs variant="line" colorScheme="blue">
            <TabList
              bg={useColorModeValue("gray.50", "gray.700")}
              borderBottom="1px"
              borderColor={useColorModeValue("gray.200", "gray.600")}
              px={6}
              pt={2}
            >
              <Tab fontSize="sm" px={4} py={2}>
                <HStack spacing={2}>
                  <Icon as={FaUser} />
                  <Text>Overview</Text>
                </HStack>
              </Tab>
              <Tab fontSize="sm" px={4} py={2}>
                <HStack spacing={2}>
                  <Icon as={FaMoneyBillWave} />
                  <Text>Compensation</Text>
                </HStack>
              </Tab>
              <Tab fontSize="sm" px={4} py={2}>
                <HStack spacing={2}>
                  <Icon as={FaFileContract} />
                  <Text>Terms</Text>
                </HStack>
              </Tab>
              <Tab fontSize="sm" px={4} py={2}>
                <HStack spacing={2}>
                  <Icon as={FaHistory} />
                  <Text>Approval</Text>
                </HStack>
              </Tab>
              <Tab fontSize="sm" px={4} py={2}>
                <HStack spacing={2}>
                  <Icon as={FaClock} />
                  <Text>Activity</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels p={6}>
              {/* Overview Tab */}
              <TabPanel p={0} pt={4}>
                <VStack spacing={6} align="stretch">
                  {/* Status Cards */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody py={4}>
                        <VStack spacing={2} align="center">
                          <Icon
                            as={CheckCircleIcon}
                            w={6}
                            h={6}
                            color="blue.500"
                          />
                          <Stat textAlign="center">
                            <StatLabel fontSize="xs" color="gray.600">
                              Status
                            </StatLabel>
                            <StatNumber fontSize="md" fontWeight="semibold">
                              {getStatusBadge(header.status)}
                            </StatNumber>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody py={4}>
                        <VStack spacing={2} align="center">
                          <Icon
                            as={CalendarIcon}
                            w={6}
                            h={6}
                            color="green.500"
                          />
                          <Stat textAlign="center">
                            <StatLabel fontSize="xs" color="gray.600">
                              Valid Till
                            </StatLabel>
                            <StatNumber fontSize="md" fontWeight="semibold">
                              {new Date(header.validTill).toLocaleDateString()}
                            </StatNumber>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody py={4}>
                        <VStack spacing={2} align="center">
                          <Icon
                            as={FaMoneyBillWave}
                            w={6}
                            h={6}
                            color="purple.500"
                          />
                          <Stat textAlign="center">
                            <StatLabel fontSize="xs" color="gray.600">
                              CTC
                            </StatLabel>
                            <StatNumber fontSize="md" fontWeight="semibold">
                              {maskSensitiveData(
                                formatCurrency(details.ctc, details.currency),
                                isCompensationPrivate
                              )}
                            </StatNumber>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* Position Details */}
                  <Card
                    bg={useColorModeValue("white", "gray.700")}
                    shadow="sm"
                    borderRadius="md"
                    border="1px"
                    borderColor={useColorModeValue("gray.200", "gray.600")}
                  >
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <Text fontSize="lg" fontWeight="semibold">
                          Position Details
                        </Text>

                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                          <VStack align="start" spacing={3}>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Designation:
                              </Text>
                              <Text fontSize="sm">
                                {details.designation || "NA"}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Department:
                              </Text>
                              <Text fontSize="sm">
                                {details.department || "NA"}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Location:
                              </Text>
                              <Text fontSize="sm">
                                {details.location || "NA"}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Type:
                              </Text>
                              <Text fontSize="sm">
                                {details.employmentType || "NA"}
                              </Text>
                            </HStack>
                          </VStack>

                          <VStack align="start" spacing={3}>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Level:
                              </Text>
                              <Text fontSize="sm">
                                {details.jobLevel || "NA"}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Joining:
                              </Text>
                              <Text fontSize="sm">
                                {details.joiningDate
                                  ? new Date(
                                      details.joiningDate
                                    ).toLocaleDateString()
                                  : "NA"}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Work Loc:
                              </Text>
                              <Text fontSize="sm">
                                {details.workLocation || "NA"}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.600"
                                minW="80px"
                              >
                                Manager:
                              </Text>
                              <Text fontSize="sm">
                                {details.reportingManager?.name || "NA"}
                              </Text>
                            </HStack>
                          </VStack>
                        </SimpleGrid>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Acceptance Status */}
                  {acceptance && (
                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Text fontSize="lg" fontWeight="semibold">
                            Acceptance Status
                          </Text>

                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                            <VStack align="start" spacing={2}>
                              <HStack spacing={2}>
                                <Text
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color="gray.600"
                                >
                                  Status:
                                </Text>
                                {getStatusBadge(acceptance.acceptanceStatus)}
                              </HStack>
                              {acceptance.acceptedAt && (
                                <HStack spacing={2}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color="gray.600"
                                  >
                                    Accepted:
                                  </Text>
                                  <Text fontSize="sm">
                                    {new Date(
                                      acceptance.acceptedAt
                                    ).toLocaleDateString()}
                                  </Text>
                                </HStack>
                              )}
                            </VStack>

                            <VStack align="start" spacing={2}>
                              {acceptance.declinedAt && (
                                <HStack spacing={2}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color="gray.600"
                                  >
                                    Declined:
                                  </Text>
                                  <Text fontSize="sm">
                                    {new Date(
                                      acceptance.declinedAt
                                    ).toLocaleDateString()}
                                  </Text>
                                </HStack>
                              )}
                              {acceptance.comments && (
                                <VStack align="start" spacing={1}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color="gray.600"
                                  >
                                    Comments:
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    bg="gray.50"
                                    p={2}
                                    borderRadius="md"
                                    w="full"
                                  >
                                    {acceptance.comments}
                                  </Text>
                                </VStack>
                              )}
                            </VStack>
                          </SimpleGrid>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}
                </VStack>
              </TabPanel>

              {/* Compensation Tab */}
              <TabPanel p={0} pt={4}>
                <VStack spacing={6} align="stretch">
                  {/* Privacy Toggle */}
                  <Card
                    bg={useColorModeValue("white", "gray.700")}
                    shadow="sm"
                    borderRadius="md"
                    border="1px"
                    borderColor={useColorModeValue("gray.200", "gray.600")}
                  >
                    <CardBody py={4}>
                      <HStack justify="space-between" align="center">
                        <HStack spacing={3}>
                          <Icon
                            as={isCompensationPrivate ? LockIcon : ViewIcon}
                            w={5}
                            h={5}
                            color={
                              isCompensationPrivate ? "red.500" : "green.500"
                            }
                          />
                          <Box>
                            <Text fontSize="sm" fontWeight="semibold">
                              Privacy Mode
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {isCompensationPrivate
                                ? "Sensitive compensation data is hidden"
                                : "All compensation details are visible"}
                            </Text>
                          </Box>
                        </HStack>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel
                            htmlFor="privacy-toggle"
                            mb="0"
                            fontSize="sm"
                          >
                            {isCompensationPrivate ? "Private" : "Public"}
                          </FormLabel>
                          <Switch
                            id="privacy-toggle"
                            colorScheme="red"
                            isChecked={isCompensationPrivate}
                            onChange={(e) =>
                              setIsCompensationPrivate(e.target.checked)
                            }
                          />
                        </FormControl>
                      </HStack>
                    </CardBody>
                  </Card>
                  {/* CTC Overview */}
                  <Card
                    bg={useColorModeValue("white", "gray.700")}
                    shadow="sm"
                    borderRadius="md"
                    border="1px"
                    borderColor={useColorModeValue("gray.200", "gray.600")}
                  >
                    <CardBody py={6}>
                      <VStack spacing={3} align="center">
                        <Icon
                          as={FaMoneyBillWave}
                          w={8}
                          h={8}
                          color="purple.500"
                        />
                        <Stat textAlign="center">
                          <StatLabel fontSize="sm" color="gray.600">
                            Cost to Company (CTC)
                          </StatLabel>
                          <StatNumber
                            fontSize="2xl"
                            fontWeight="bold"
                            color="purple.600"
                          >
                            {maskSensitiveData(
                              formatCurrency(details.ctc, details.currency),
                              isCompensationPrivate
                            )}
                          </StatNumber>
                          <StatHelpText fontSize="sm">
                            Annual compensation package
                          </StatHelpText>
                        </Stat>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Salary Breakdown */}
                  <Card
                    bg={useColorModeValue("white", "gray.700")}
                    shadow="sm"
                    borderRadius="md"
                    border="1px"
                    borderColor={useColorModeValue("gray.200", "gray.600")}
                  >
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <Text fontSize="lg" fontWeight="semibold">
                          Monthly Salary Breakdown
                        </Text>

                        <Box overflowX="auto">
                          <Table variant="simple" size="sm">
                            <Thead>
                              <Tr>
                                <Th
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="gray.700"
                                >
                                  Component
                                </Th>
                                <Th
                                  isNumeric
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="gray.700"
                                >
                                  Amount ({details.currency})
                                </Th>
                                <Th
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="gray.700"
                                >
                                  %
                                </Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {Object.entries(
                                details.salaryBreakdown || {}
                              ).map(([key, value]) => {
                                if (!value || value === 0) return null;
                                const percentage = (
                                  (value / details.ctc) *
                                  100
                                ).toFixed(1);
                                return (
                                  <Tr key={key}>
                                    <Td
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {key.replace(/([A-Z])/g, " $1").trim()}
                                    </Td>
                                    <Td isNumeric fontWeight="semibold">
                                      {maskSensitiveData(
                                        value.toLocaleString(),
                                        isCompensationPrivate
                                      )}
                                    </Td>
                                    <Td>
                                      <HStack spacing={2}>
                                        <Text fontWeight="medium">
                                          {percentage}%
                                        </Text>
                                        <Progress
                                          value={parseFloat(percentage)}
                                          size="sm"
                                          colorScheme="green"
                                        />
                                      </HStack>
                                    </Td>
                                  </Tr>
                                );
                              })}
                            </Tbody>
                          </Table>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Benefits */}
                  {details.benefits && details.benefits.length > 0 && (
                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Text fontSize="lg" fontWeight="semibold">
                            Benefits & Perks
                          </Text>

                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            {details.benefits.map((benefit, index) => (
                              <HStack
                                key={index}
                                spacing={2}
                                p={2}
                                bg="green.50"
                                borderRadius="md"
                              >
                                <Circle size="4px" bg="green.500" />
                                <Text fontSize="sm">{benefit}</Text>
                              </HStack>
                            ))}
                          </SimpleGrid>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}
                </VStack>
              </TabPanel>

              {/* Terms & Conditions Tab */}
              <TabPanel p={0} pt={4}>
                <VStack spacing={4} align="stretch">
                  {clauses && clauses.length > 0 ? (
                    clauses.map((clause, index) => (
                      <Card
                        key={clause._id}
                        bg={useColorModeValue("white", "gray.700")}
                        shadow="sm"
                        borderRadius="md"
                        border="1px"
                        borderColor={useColorModeValue("gray.200", "gray.600")}
                      >
                        <CardBody>
                          <VStack spacing={3} align="stretch">
                            <HStack spacing={2}>
                              <Circle size="24px" bg="blue.50" color="blue.500">
                                <Text fontSize="xs" fontWeight="bold">
                                  {index + 1}
                                </Text>
                              </Circle>
                              <Box flex={1}>
                                <Text fontSize="md" fontWeight="semibold">
                                  {clause.title}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  Updated{" "}
                                  {new Date(
                                    clause.updatedAt || clause.createdAt
                                  ).toLocaleDateString()}
                                </Text>
                              </Box>
                            </HStack>

                            <Box
                              bg={useColorModeValue("gray.50", "gray.600")}
                              p={3}
                              borderRadius="md"
                              borderLeft="3px"
                              borderColor="blue.500"
                            >
                              <Text fontSize="sm" lineHeight="1.5">
                                {clause.content}
                              </Text>
                            </Box>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody py={8}>
                        <VStack spacing={3} align="center">
                          <Icon
                            as={FaFileContract}
                            w={8}
                            h={8}
                            color="gray.400"
                          />
                          <Text
                            fontSize="md"
                            color="gray.500"
                            textAlign="center"
                          >
                            No terms and conditions found
                          </Text>
                          <Text
                            fontSize="sm"
                            color="gray.400"
                            textAlign="center"
                          >
                            Terms and conditions will be displayed here once
                            added
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}
                </VStack>
              </TabPanel>

              {/* Approval History Tab */}
              <TabPanel p={0} pt={4}>
                <VStack spacing={4} align="stretch">
                  {header.approvals && header.approvals.length > 0 ? (
                    header.approvals.map((approval, index) => (
                      <Card
                        key={index}
                        bg={useColorModeValue("white", "gray.700")}
                        shadow="sm"
                        borderRadius="md"
                        border="1px"
                        borderColor={useColorModeValue("gray.200", "gray.600")}
                      >
                        <CardBody>
                          <VStack spacing={3} align="stretch">
                            <HStack justify="space-between" align="start">
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  name={approval.approverName}
                                  bg={
                                    approval.status === "approved"
                                      ? "green.500"
                                      : approval.status === "rejected"
                                      ? "red.500"
                                      : "yellow.500"
                                  }
                                  color="white"
                                />
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold">
                                    Level {approval.approvalLevel}:{" "}
                                    {approval.approverName}
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    {approval.approverRole}
                                  </Text>
                                </Box>
                              </HStack>

                              <VStack align="end" spacing={1}>
                                {getStatusBadge(approval.status)}
                                {approval.approvedAt && (
                                  <Text fontSize="xs" color="gray.500">
                                    {new Date(
                                      approval.approvedAt
                                    ).toLocaleDateString()}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>

                            {approval.comments && (
                              <Box
                                bg={useColorModeValue("gray.50", "gray.600")}
                                p={2}
                                borderRadius="md"
                                borderLeft="3px"
                                borderColor={
                                  approval.status === "approved"
                                    ? "green.500"
                                    : approval.status === "rejected"
                                    ? "red.500"
                                    : "yellow.500"
                                }
                              >
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  mb={1}
                                >
                                  Comments:
                                </Text>
                                <Text fontSize="xs">{approval.comments}</Text>
                              </Box>
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody py={8}>
                        <VStack spacing={3} align="center">
                          <Icon as={FaHistory} w={8} h={8} color="gray.400" />
                          <Text
                            fontSize="md"
                            color="gray.500"
                            textAlign="center"
                          >
                            No approval workflow configured
                          </Text>
                          <Text
                            fontSize="sm"
                            color="gray.400"
                            textAlign="center"
                          >
                            Approval history will be displayed here once the
                            approval process begins
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}
                </VStack>
              </TabPanel>

              {/* Activity Log Tab */}
              <TabPanel p={0} pt={4}>
                <VStack spacing={4} align="stretch">
                  {recentLogs && recentLogs.length > 0 ? (
                    recentLogs.map((log) => (
                      <Card
                        key={log._id}
                        bg={useColorModeValue("white", "gray.700")}
                        shadow="sm"
                        borderRadius="md"
                        border="1px"
                        borderColor={useColorModeValue("gray.200", "gray.600")}
                      >
                        <CardBody>
                          <VStack spacing={2} align="stretch">
                            <HStack justify="space-between" align="start">
                              <HStack spacing={2}>
                                <Circle
                                  size="32px"
                                  bg={
                                    log.action === "created"
                                      ? "green.50"
                                      : log.action === "sent"
                                      ? "blue.50"
                                      : log.action === "accepted"
                                      ? "purple.50"
                                      : log.action === "rejected"
                                      ? "red.50"
                                      : "gray.50"
                                  }
                                  color={
                                    log.action === "created"
                                      ? "green.500"
                                      : log.action === "sent"
                                      ? "blue.500"
                                      : log.action === "accepted"
                                      ? "purple.500"
                                      : log.action === "rejected"
                                      ? "red.500"
                                      : "gray.500"
                                  }
                                >
                                  <Icon
                                    as={
                                      log.action === "created"
                                        ? CheckCircleIcon
                                        : log.action === "sent"
                                        ? EmailIcon
                                        : log.action === "accepted"
                                        ? CheckCircleIcon
                                        : log.action === "rejected"
                                        ? WarningIcon
                                        : InfoIcon
                                    }
                                    w={4}
                                    h={4}
                                  />
                                </Circle>
                                <Box>
                                  <Badge
                                    colorScheme={
                                      log.action === "created"
                                        ? "green"
                                        : log.action === "sent"
                                        ? "blue"
                                        : log.action === "accepted"
                                        ? "purple"
                                        : log.action === "rejected"
                                        ? "red"
                                        : "gray"
                                    }
                                    fontSize="xs"
                                    mb={1}
                                  >
                                    {log.action
                                      .replace(/_/g, " ")
                                      .toUpperCase()}
                                  </Badge>
                                  <Text fontSize="xs" color="gray.600">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </Text>
                                </Box>
                              </HStack>
                            </HStack>

                            <Box
                              bg={useColorModeValue("gray.50", "gray.600")}
                              p={2}
                              borderRadius="md"
                              borderLeft="3px"
                              borderColor={
                                log.action === "created"
                                  ? "green.500"
                                  : log.action === "sent"
                                  ? "blue.500"
                                  : log.action === "accepted"
                                  ? "purple.500"
                                  : log.action === "rejected"
                                  ? "red.500"
                                  : "gray.500"
                              }
                            >
                              <Text fontSize="sm" mb={1}>
                                {log.remarks}
                              </Text>
                              {log.performedByName && (
                                <Text fontSize="xs" color="gray.500">
                                  By: {log.performedByName}
                                </Text>
                              )}
                            </Box>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <Card
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      borderRadius="md"
                      border="1px"
                      borderColor={useColorModeValue("gray.200", "gray.600")}
                    >
                      <CardBody py={8}>
                        <VStack spacing={3} align="center">
                          <Icon as={FaClock} w={8} h={8} color="gray.400" />
                          <Text
                            fontSize="md"
                            color="gray.500"
                            textAlign="center"
                          >
                            No activity logs found
                          </Text>
                          <Text
                            fontSize="sm"
                            color="gray.400"
                            textAlign="center"
                          >
                            Activity logs will be displayed here as actions are
                            performed
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default OfferDetailsModal;
